import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../lib/api';
import type { User } from '../types';

interface UsersResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: User[];
}

// Hook for fetching organization users
export const useOrganizationUsers = () => {
  return useQuery<User[]>({
    queryKey: ['organizationUsers'],
    queryFn: async () => {
      const response = await apiClient.get<UsersResponse>('/api/users-list/');
      return Array.isArray(response.data) ? response.data : (response.data.results || []);
    },
    staleTime: 5 * 60 * 1000, // Consider data stale after 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });
};

// Helper function to format user for display
export const formatUserName = (user: User): string => {
  if (user.first_name || user.last_name) {
    return `${user.first_name} ${user.last_name}`.trim();
  }
  return user.username;
};

// Helper function to get user mention display
export const getUserMention = (user: User): string => {
  return `@${user.username}`;
};

// Parse mentions from text
export const parseMentions = (text: string): string[] => {
  const mentionRegex = /@(\w+)/g;
  const matches = text.match(mentionRegex);
  return matches ? matches.map(m => m.substring(1)) : [];
};

// Check if a user is mentioned in text
export const isUserMentioned = (text: string, username: string): boolean => {
  const mentions = parseMentions(text);
  return mentions.includes(username);
};